import { Box, Grid, ResponsiveContext } from 'grommet';
import React, { useState, useContext, useEffect, PropsWithChildren } from 'react';
import styled from 'styled-components/macro';
import Header from '../Header/Header';
import { ScreenSize, screenSizeBiggerOrEqual, isLargeOrSmaller } from '../../../../lib/screen';
import Sidebar from '../Sidebar/Sidebar';
import SidebarCategoryItem from '../SidebarItem/SidebarCategoryItem';
import SidebarLinkItem from '../SidebarItem/SidebarLinkItem';
import { useAuth } from '../../../../base/auth/useAuth';
import { AdminAuthContext } from '../../../auth/authContext';
import { isProduction } from '../../../../lib/environment';

import { base, useAgnosticGuiContext, Platform } from '@sdxcorp/sdx-dds-gui-lib';
import { AdminAbilities } from '../../../auth/abilities';

const {
  atoms: {
    Icons: { AssetIssuance, Dashboard, File, Transactions, Holdings, Coin, Bank, Settings, Info },
  },
} = base;

const StyledGrid = styled(Grid)`
  height: 100vh;
`;

const StyledBox = styled(Box)`
  background-color: ${props => props.theme.global.colors['con-background']};
`;

const MainBox = styled(Box)`
  position: relative;
`;

const Layout: React.FC<PropsWithChildren> = ({ children }) => {
  const size = useContext(ResponsiveContext);
  const [sidebarVisible, setSidebarVisible] = useState(screenSizeBiggerOrEqual(ScreenSize.TABLET_LANDSCAPE));
  const [userSetVisible, setUserSetVisible] = useState<boolean | undefined>();
  const toggleSidebar = () => {
    const newVisibility = !sidebarVisible;
    if (!isLargeOrSmaller(size)) {
      setUserSetVisible(newVisibility);
    }
    setSidebarVisible(newVisibility);
  };
  const closeSidebarIfNeeded = () => {
    if (!screenSizeBiggerOrEqual(ScreenSize.TABLET_LANDSCAPE)) {
      setSidebarVisible(false);
    }
  };
  const { loading, canI } = useAuth<AdminAbilities>(AdminAuthContext);
  const { setPlatform } = useAgnosticGuiContext();

  useEffect(() => {
    if (loading) {
      return;
    }
    if (canI('asset-types-cryptocurrency')) {
      setPlatform(Platform.CRYPTO);
    } else {
      setPlatform(Platform.FMI);
    }
  }, [loading, canI, setPlatform]);

  return (
    <StyledGrid
      areas={[
        { name: 'header', start: [0, 0], end: [0, 0] },
        { name: 'main', start: [0, 1], end: [0, 1] },
      ]}
      columns={['auto']}
      rows={['64px', 'auto']}
      gap="0"
    >
      <Header gridArea="header" onMenuClick={toggleSidebar} />

      <MainBox gridArea="main" flex direction="row">
        <Sidebar
          size={size}
          visible={sidebarVisible}
          userSetVisible={userSetVisible}
          width="320px"
          onLinkClick={closeSidebarIfNeeded}
          setSidebarVisible={setSidebarVisible}
        >
          {!isProduction() ? (
            <SidebarCategoryItem id="menu-category-library" label="Library" icon={<Info />} to="/admin/library/" match="/library" matchExact={true} />
          ) : null}
          {!canI('asset-types-cryptocurrency') && (
            <SidebarCategoryItem id="menu-category-dashboard" label="Dashboard" icon={<Dashboard />} to="/admin/" match="/admin" matchExact={true} />
          )}
          <SidebarCategoryItem
            id="menu-category-refdata"
            label="Reference data"
            icon={<File />}
            match="/admin/(reference-data|assets|members|memberSubscriptions|transferRestriction|eligibilityOnRestrictedAsset|listingRequest|fractionalisationAssets|accounts|externalAccounts|standing-settlement-instructions|currencies|LegalEntity)"
          >
            <SidebarLinkItem testId="menu-subcategory-assets" label="Assets" to="/admin/assets" match="/admin/assets" />
            {(!canI('asset-types-cryptocurrency') || canI('system-operator')) && (
              <SidebarLinkItem testId="menu-subcategory-members" label="Members" to="/admin/members" match="/admin/members" />
            )}
            {!canI('asset-types-cryptocurrency') && (
              <SidebarLinkItem
                testId="menu-subcategory-member-subscriptions"
                label="Member Subscription"
                to="/admin/memberSubscriptions"
                match="/admin/memberSubscriptions"
              />
            )}
            <SidebarLinkItem
              testId="menu-subcategory-transfer-restrictions"
              label="Transfer Restrictions"
              to="/admin/transferRestriction"
              match="/admin/transferRestriction"
            />
            <SidebarLinkItem
              testId="menu-subcategory-eligibility-on-restricted-asset"
              label="Assets Eligibility"
              to="/admin/eligibilityOnRestrictedAsset"
              match="/admin/eligibilityOnRestrictedAsset"
            />
            <SidebarLinkItem
              testId="menu-subcategory-fractionalisation-assets"
              label="Fractionalisation Asset List"
              to="/admin/fractionalisationAssets"
              match="/admin/fractionalisationAssets"
            />
            <SidebarLinkItem testId="menu-subcategory-accounts" label="Accounts" to="/admin/accounts" match="/admin/accounts" />
            {!canI('asset-types-cryptocurrency') && (
              <SidebarLinkItem
                testId="menu-subcategory-external-accounts"
                label="External Accounts"
                to="/admin/externalAccounts"
                match="/admin/externalAccounts"
              />
            )}
            {canI('asset-types-cryptocurrency') && (
              <SidebarLinkItem
                testId="menu-subcategory-externalEntities"
                label="External Entities"
                to="/admin/externalEntities"
                match="/admin/externalEntities"
              />
            )}
            {/* {canI('system-operator') && !canI('asset-types-cryptocurrency') && (
              <SidebarLinkItem
                testId="menu-subcategory-standingSettlementInstruction"
                label="On-Exchange Trades SSI"
                to={adminRouteTo.standingSettlementInstructionsOverview()}
                match={adminRouteTo.standingSettlementInstructionsOverview()}
              />
            )} */}
            {canI('asset-types-cryptocurrency') && (
              <SidebarLinkItem
                testId="menu-subcategory-whitelistedAddresses"
                label="Whitelisted Addresses"
                to="/admin/whitelistedAddresses"
                match="/admin/whitelistedAddresses"
              />
            )}
            {canI('asset-types-cryptocurrency') && canI('system-operator') && (
              <SidebarLinkItem
                testId="menu-subcategory-blockchainPlatforms"
                label="Blockchain Platforms"
                to="/admin/reference-data/BlockchainPlatform/overview"
                match="/admin/reference-data/BlockchainPlatform"
              />
            )}
            {(!canI('asset-types-cryptocurrency') || canI('system-operator')) && (
              <SidebarLinkItem
                testId="menu-subcategory-legalEntities"
                label="Legal Entities"
                to="/admin/LegalEntity/overview"
                match="/admin/LegalEntity"
              />
            )}
            {!canI('asset-types-cryptocurrency') && (
              <SidebarLinkItem testId="menu-subcategory-currencies" label="Currencies" to="/admin/currencies" match="/admin/currencies" />
            )}
            {canI('system-operator') && !canI('asset-types-cryptocurrency') && (
              <SidebarLinkItem
                testId="menu-subcategory-Depository"
                label="Depositories"
                to="/admin/reference-data/CentralSecuritiesDepository/overview"
                match="/admin/reference-data/CentralSecuritiesDepository/overview"
              />
            )}
          </SidebarCategoryItem>
          <SidebarCategoryItem id="menu-category-transactions" label="Transactions" icon={<Transactions />} match="/admin/transactions">
            <SidebarLinkItem
              testId="menu-subcategory-settlement_instructions"
              label="Settlement Instructions"
              to="/admin/transactions/settlement-instructions"
              match="/admin/transactions/settlement-instructions"
            />
            {!canI('asset-types-cryptocurrency') && (
              <SidebarLinkItem
                testId="menu-subcategory-payment_instructions"
                label="Payment Instructions"
                to="/admin/transactions/payment-instructions"
                match="/admin/transactions/payment-instructions"
              />
            )}
            {canI('asset-types-cryptocurrency') && (
              <SidebarLinkItem
                testId="menu-subcategory-deposit_withdrawal"
                label="Deposits/Withdrawals"
                to="/admin/transactions/deposit-withdrawal"
                match="/admin/transactions/deposit-withdrawal"
              />
            )}
          </SidebarCategoryItem>
          {canI('technical-issuer-agent') && !canI('asset-types-cryptocurrency') && (
            <SidebarCategoryItem id="menu-category-technical-issuer" label="Technical Issuer" icon={<Coin />} match="/admin/technical-issuer">
              <SidebarLinkItem
                testId="menu-subcategory-asset_transactions"
                label="Asset Transactions"
                to="/admin/technical-issuer/asset-transactions"
                match="/admin/technical-issuer/asset-transactions"
              />
              <SidebarLinkItem
                testId="menu-subcategory-cash_transactions"
                label="Cash Transactions"
                to="/admin/technical-issuer/cash-transactions"
                match="/admin/technical-issuer/cash-transactions"
              />
              <SidebarLinkItem
                testId="menu-subcategory-outstanding_tokens"
                label="Cash Tokens Issued"
                to="/admin/technical-issuer/outstanding-tokens"
                match="/admin/technical-issuer/outstanding-tokens"
              />
              <SidebarLinkItem
                testId="menu-subcategory-member_balance_report"
                label="Cash by Member"
                to="/admin/technical-issuer/member-balance-report"
                match="/admin/technical-issuer/member-balance-report"
              />
              <SidebarLinkItem
                testId="menu-subcategory-member-account_balance_report"
                label="Cash by Member by Account"
                to="/admin/technical-issuer/member-account-balance-report"
                match="/admin/technical-issuer/member-account-balance-report"
              />
              {canI('snb-agent') && (
                <SidebarLinkItem
                  testId="menu-subcategory-eligibility_list"
                  label="Currency Eligibility"
                  to="/admin/technical-issuer/currency-eligibility-list"
                  match="/admin/technical-issuer/currency-eligibility-list"
                />
              )}
              <SidebarLinkItem
                testId="menu-subcategory-movement_report"
                label="Movement Report"
                to="/admin/technical-issuer/movement-report"
                match="/admin/technical-issuer/movement-report"
              />
              <SidebarLinkItem
                testId="menu-subcategory-settlement_instruction_report"
                label="Settlement Instruction Report"
                to="/admin/technical-issuer/settlement-instruction-report"
                match="/admin/technical-issuer/settlement-instruction-report"
              />
              <SidebarLinkItem
                testId="menu-subcategory-payment_instruction_report"
                label="Payment Instruction Report"
                to="/admin/technical-issuer/payment-instruction-report"
                match="/admin/technical-issuer/payment-instruction-report"
              />
            </SidebarCategoryItem>
          )}
          <SidebarCategoryItem id="menu-category-holdings" label="Holdings" to="/admin/holdings" match="/admin/holdings" icon={<Holdings />}>
            <SidebarLinkItem
              testId="menu-subcategory-holdings_current"
              label="Current"
              to="/admin/holdings/current"
              match="/admin/holdings/current"
            />
            <SidebarLinkItem
              testId="menu-subcategory-holdings_historical"
              label="Historical"
              to="/admin/holdings/historical"
              match="/admin/holdings/historical"
            />
          </SidebarCategoryItem>
          <SidebarCategoryItem
            id="menu-category-movements"
            label="Movements"
            to="/admin/movements"
            match="/admin/movements"
            icon={<AssetIssuance />}
          />
          {!canI('asset-types-cryptocurrency') && (
            <SidebarCategoryItem
              id="menu-category-corporate-actions"
              label="Corporate Actions"
              match="/admin/(corporate-actions|corporate-action-profiles)"
              icon={<Bank />}
            >
              <SidebarLinkItem
                testId="menu-subcategory-corporate-actions"
                label="C/A Overview"
                to="/admin/corporate-actions"
                match="/admin/corporate-actions"
              />
              <SidebarLinkItem
                testId="menu-subcategory-corporate-action-profile"
                label="C/A Profile"
                to="/admin/corporate-action-profiles"
                match="/admin/corporate-action-profiles"
              />
            </SidebarCategoryItem>
          )}
          <SidebarCategoryItem id="menu-category-operations" label="Operations" icon={<Settings />} match="/admin/operations">
            {/* {canI('system-operator') && !canI('asset-types-cryptocurrency') && (
              <SidebarLinkItem
                testId="menu-subcategory-trading-participant"
                label="Trading Participants"
                to="/admin/operations/trading-participants"
                match="/admin/operations/trading-participants"
              />
            )} */}
            {(!canI('asset-types-cryptocurrency') || canI('system-operator')) && (
              <SidebarLinkItem
                testId="menu-subcategory-subbalance-transfers"
                label="Cash Sub-balance Transfer"
                to="/admin/operations/subbalance-transfers"
                match="/admin/operations/subbalance-transfers"
              />
            )}
            {(!canI('asset-types-cryptocurrency') || canI('system-operator')) && (
              <SidebarLinkItem
                testId="menu-subcategory-asset-subbalance-transfers"
                label="Asset Sub-balance Transfer"
                to="/admin/operations/asset-subbalance-transfers"
                match="/admin/operations/asset-subbalance-transfers"
              />
            )}
            {canI('system-operator') && (
              <SidebarLinkItem
                testId="menu-subcategory-taxIncomeCodes"
                label="Tax Income Codes"
                to="/admin/operations/taxIncomeCodes"
                match="/admin/operations/taxIncomeCodes"
              />
            )}
            {canI('system-operator') && (
              <SidebarLinkItem
                testId="menu-subcategory-taxExemptionCodes"
                label="Tax Exemption Codes"
                to="/admin/operations/taxExemptionCodes"
                match="/admin/operations/taxExemptionCodes"
              />
            )}
            <SidebarLinkItem
              testId="menu-subcategory-scheduler"
              label="Scheduler"
              to="/admin/operations/scheduler"
              match="/admin/operations/scheduler"
            />
            <SidebarLinkItem
              testId="menu-subcategory-business-audit-log"
              label="Business Audit Log"
              to="/admin/operations/audit-log"
              match="/admin/operations/audit-log"
            />
            {(!canI('asset-types-cryptocurrency') || canI('system-operator')) && (
              <SidebarLinkItem
                testId="menu-subcategory-business-calendar"
                label="Business Calendars"
                to="/admin/operations/business-calendar"
                match="/admin/operations/business-calendar"
              />
            )}
            {(!canI('asset-types-cryptocurrency') || canI('system-operator')) && (
              <SidebarLinkItem
                testId="menu-subcategory-business_calendar-day"
                label="Business Calendar Days"
                to="/admin/operations/business-calendar-day"
                match="/admin/operations/business-calendar-day"
              />
            )}
            {canI('onboarding:onboardMember') && (
              <SidebarLinkItem
                testId="menu-subcategory-onboarding"
                label="Onboarding"
                to="/admin/operations/onboarding"
                match="/admin/operations/onboarding"
              />
            )}
          </SidebarCategoryItem>
        </Sidebar>
        <StyledBox fill overflow="auto" style={{ display: 'block' }} id="layout-content">
          {!loading && children}
        </StyledBox>
      </MainBox>
    </StyledGrid>
  );
};

export default Layout;
